<template>
  <header class="bg-white shadow-sm sticky top-0 z-50">
    <div class="container mx-auto px-4">
      <div class="flex justify-between items-center h-24">
        <!-- Logo -->
        <div class="flex-shrink-0 flex items-center">
          <router-link to="/" class="flex items-center">
            <img src="/img/logo evt 2.png" alt="Eagle's Vision" class="h-20 w-auto object-contain">
          </router-link>
        </div>

        <!-- Desktop Menu -->
        <nav class="hidden md:flex space-x-8 items-center">
          <router-link to="/#vuelos" class="text-gray-600 hover:text-primary transition-colors">{{ $t('header.flights') }}</router-link>
          <router-link to="/paquetes" class="text-gray-600 hover:text-primary transition-colors">{{ $t('header.packages') }}</router-link>
          <router-link to="/nosotros" class="text-gray-600 hover:text-primary transition-colors">{{ $t('header.about') }}</router-link>
          
           <!-- Language Switcher -->
            <div class="relative group">
                <button class="flex items-center space-x-1 text-gray-500 hover:text-primary focus:outline-none py-2">
                    <span class="uppercase font-bold">{{ $i18n.locale }}</span>
                    <span class="material-icons text-sm">expand_more</span>
                </button>
                <div class="absolute right-0 mt-0 w-24 bg-white rounded-md shadow-lg py-1 z-50 hidden group-hover:block hover:block border border-gray-100">
                    <button @click="changeLanguage('es')" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">ES</button>
                    <button @click="changeLanguage('en')" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 font-medium">EN</button>
                </div>
            </div>

          <!-- Login Button (when NOT authenticated) -->
          <button 
            v-if="!isAuthenticated"
            :key="'login-btn-' + isAuthenticated"
            @click="SET_SHOW_AUTH_MODAL(true)"
            class="bg-primary text-white px-6 py-2 rounded-full hover:bg-opacity-90 transition-colors font-medium shadow-md"
          >
            {{ $t('header.login') }}
          </button>
          
          <!-- User Menu (when authenticated) -->
          <div v-if="isAuthenticated" :key="'user-menu-' + isAuthenticated" class="relative" @click.stop="toggleProfileMenu">
             <button class="flex items-center space-x-2 focus:outline-none">
                <div class="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg shadow-sm">
                   {{ userInitials }}
                </div>
                <div class="hidden lg:block text-left">
                   <div class="text-sm font-bold text-gray-700 leading-tight">{{ currentUser ? currentUser.name : 'User' }}</div>
                   <div class="text-xs text-gray-500">{{ $t('header.my_account') }}</div>
                </div>
                <svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
             </button>

              <!-- Dropdown Menu -->
             <div v-if="showProfileMenu" class="absolute right-0 mt-3 w-64 bg-white rounded-xl shadow-2xl py-2 ring-1 ring-black ring-opacity-5 z-50 transform origin-top-right transition-all animate-fade-in-down border border-gray-100">
                
                <!-- Header -->
                <div class="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
                    <p class="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">{{ $t('header.connected_as') }}</p>
                   <p class="text-sm font-bold text-gray-900 truncate">{{ currentUser ? currentUser.name : 'User' }}</p>
                   <p class="text-xs text-gray-500 truncate">{{ currentUser ? currentUser.email : '' }}</p>
                </div>

                <div class="py-1">
                    <router-link to="/mis-cotizaciones" class="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors group">
                       <span class="bg-blue-50 text-blue-600 p-2 rounded-lg mr-3 group-hover:bg-primary group-hover:text-white transition-colors">
                            <span class="material-icons text-sm">history</span>
                       </span>
                       <div>
                            <span class="font-bold display-block">{{ $t('header.my_quotes') }}</span>
                            <span class="text-xs text-gray-400 block">{{ $t('header.view_history') }}</span>
                       </div>
                    </router-link>

                    <!-- Profile Link -->
                     <router-link to="/perfil" class="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors group text-left">
                       <span class="bg-purple-50 text-purple-600 p-2 rounded-lg mr-3 group-hover:bg-primary group-hover:text-white transition-colors">
                            <span class="material-icons text-sm">person</span>
                       </span>
                       <div>
                            <span class="font-bold display-block">{{ $t('header.my_profile') }}</span>
                            <span class="text-xs text-gray-400 block">{{ $t('header.personal_data') }}</span>
                       </div>
                    </router-link>
                    
                    <!-- Receive Offers Toggle -->
                    <div class="px-4 py-2 border-t border-gray-50">
                        <label class="flex items-center justify-between cursor-pointer group">
                             <div class="text-sm">
                                <span class="font-medium text-gray-700 block">{{ $t('header.receive_offers') }}</span>
                                <span class="text-xs text-gray-400">{{ $t('header.in_my_email') }}</span>
                             </div>
                             <div class="relative">
                                <input type="checkbox" v-model="receiveOffers" class="sr-only" @change="toggleOffers">
                                <div class="w-10 h-6 bg-gray-200 rounded-full shadow-inner transition-colors" :class="{ 'bg-green-500': receiveOffers }"></div>
                                <div class="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow transition-transform" :class="{ 'transform translate-x-4': receiveOffers }"></div>
                             </div>
                        </label>
                    </div>
                </div>

                <div class="border-t border-gray-100 mt-1 py-1">
                    <button @click="logout" class="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                       <span class="material-icons text-sm mr-2">logout</span> {{ $t('header.logout') }}
                    </button>
                </div>
             </div>
             
             <!-- Backdrop to close -->
             <div v-if="showProfileMenu" class="fixed inset-0 z-40" @click.stop="showProfileMenu = false"></div>
          </div>
          
          <!-- No Auth Modal here anymore, it's in App.vue -->
        </nav>

        <!-- Mobile Menu Button -->
        <div class="md:hidden flex items-center">
          <button @click="isOpen = !isOpen" class="text-gray-600 hover:text-primary focus:outline-none">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path v-if="!isOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div v-if="isOpen" class="md:hidden bg-white border-t">
      <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
        <router-link to="/#vuelos" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50">{{ $t('header.flights') }}</router-link>
        <router-link to="/paquetes" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50">{{ $t('header.packages') }}</router-link>
        <router-link to="/nosotros" class="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50">{{ $t('header.about') }}</router-link>
        
        <!-- Not Authenticated -->
         <button 
            v-if="!isAuthenticated"
            @click="SET_SHOW_AUTH_MODAL(true); isOpen = false"
            class="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-white bg-primary hover:bg-red-600 mt-4"
         >
           <span class="material-icons text-sm align-middle mr-1">login</span>
           {{ $t('header.login') }}
         </button>
        
         <!-- Authenticated User Menu -->
         <div v-if="isAuthenticated" class="border-t border-gray-200 mt-4 pt-4">
           <div class="px-3 py-2 mb-2">
             <p class="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">{{ $t('header.connected_as') }}</p>
            <p class="text-sm font-bold text-gray-900 truncate">{{ currentUser ? currentUser.name : 'User' }}</p>
            <p class="text-xs text-gray-500 truncate">{{ currentUser ? currentUser.email : '' }}</p>
          </div>
          
          <router-link 
            to="/mis-cotizaciones" 
            @click.native="isOpen = false"
            class="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary"
          >
            <span class="material-icons text-sm mr-2">history</span>
            {{ $t('header.my_quotes') }}
          </router-link>
          
          <router-link 
            to="/perfil" 
            @click.native="isOpen = false"
            class="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary"
          >
            <span class="material-icons text-sm mr-2">person</span>
            {{ $t('header.my_profile') }}
          </router-link>
          
          <button 
            @click="logout; isOpen = false" 
            class="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <span class="material-icons text-sm mr-2">logout</span>
            {{ $t('header.logout') }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
// AuthModal is now global in App.vue
export default {
  name: 'TheHeader',
  // AuthModal is now global in App.vue
  data() {
    return {
      isOpen: false,
      showProfileMenu: false,
      receiveOffers: false
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'currentUser']),
    userInitials() {
        if (!this.currentUser || !this.currentUser.name) return 'U'
        return this.currentUser.name
            .split(' ')
            .map(n => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()
    }
  },
  mounted() {
    console.log('🏠 Header mounted - isAuthenticated:', this.isAuthenticated, 'user:', this.currentUser)
    if (this.currentUser && this.currentUser.email_for_notifications) {
        this.receiveOffers = true
    }
    if (this.$route.query.login === 'true') {
        this.showAuthModal = true
    }
  },
  methods: {
    ...mapActions('auth', ['logout']),
    ...mapMutations('auth', ['SET_SHOW_AUTH_MODAL']),
    async changeLanguage(lang) {
        this.$i18n.locale = lang
        localStorage.setItem('language', lang)
        
        if (this.isAuthenticated) {
            try {
                const apiClient = require('@/services/api').default
                await apiClient.put('/auth/me', { language: lang })
            } catch (e) {
                console.error('Failed to sync language', e)
            }
        }
    },

    toggleProfileMenu() {
        this.showProfileMenu = !this.showProfileMenu
    },
    async toggleOffers() {
        try {
            const apiClient = require('@/services/api').default
            await apiClient.put('/auth/me', { 
                receive_offers: this.receiveOffers 
            })
            // Update local user state implies updating the store too, but for now we just toggle UI
        } catch (error) {
            console.error('Error updating preference', error)
            this.receiveOffers = !this.receiveOffers
        }
    }
  },
  watch: {
    isAuthenticated(newVal, oldVal) {
        console.log('🔄 isAuthenticated changed:', oldVal, '→', newVal)
    },
    currentUser: {
        immediate: true,
        handler(newVal) {
            if (newVal && newVal.email_for_notifications) {
                this.receiveOffers = true
            } else {
                this.receiveOffers = false
            }
        }
    },
    $route() {
        this.isOpen = false
        this.showProfileMenu = false
        if (this.$route.query.login === 'true') {
            this.SET_SHOW_AUTH_MODAL(true)
        }
    }
  }
}
</script>
